/**
 * AutoTrigger 适配器
 * 职责：将 DOM 自动触发转换为新架构 API 调用
 *
 * ⚠️ 注意：旧埋点代码完全不动，继续走原有流程
 * 这个适配器只负责新架构的上报
 */

import { trackerV2 } from "../core/TrackerV2";
import { trackDataRegistry } from "../components/TrackDataRegistry";
import { shouldUseV2 } from "../config/migration";

export class AutoTriggerAdapter {
  /**
   * 处理自动触发的事件
   * @param eventName 事件名称（从 data-gd-click 等属性获取）
   * @param targetNode DOM 节点
   */
  handleAutoTrigger(eventName: string, targetNode: Element): void {
    // 获取页面名称
    const pageName = this.getPageName();

    // 检查是否应该使用新架构
    const { useV2 } = shouldUseV2(eventName, pageName);

    if (!useV2) {
      // 不使用新架构，直接返回（旧埋点会继续处理）
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[AutoTriggerAdapter] Event "${eventName}" not enabled for V2`
        );
      }
      return;
    }

    // 1. 从 Track 组件获取数据（新方案必须使用 Track 组件）
    const trackId = trackDataRegistry.findTrackId(targetNode);
    if (!trackId) {
      // 新方案必须使用 Track 组件，未找到则跳过
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[AutoTriggerAdapter] Track component not found for event: ${eventName}`
        );
      }
      return;
    }

    const config = trackDataRegistry.get(trackId);
    if (!config) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[AutoTriggerAdapter] Track config not found for trackId: ${trackId}`
        );
      }
      return;
    }

    if (config.event !== eventName) {
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `[AutoTriggerAdapter] Event mismatch: expected ${eventName}, got ${config.event}`
        );
      }
      return;
    }

    // 2. 调用新架构 API（数据增强器会自动注入上下文数据）
    trackerV2.track(config.event, config.data.current, {
      targetNode, // 传入 targetNode 仅用于数据增强器判断
      enrich: config.autoEnrich,
      enrichOptions: config.enrichOptions,
    });

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[AutoTriggerAdapter] Handled event: ${eventName}`,
        config.data.current
      );
    }
  }

  /**
   * 获取页面名称
   */
  private getPageName(): string {
    try {
      if (typeof window !== "undefined" && (window as any).getPage) {
        const page = (window as any).getPage();
        return page?.page_nm || "";
      }
    } catch (e) {
      // 降级处理
    }
    return "";
  }
}

// 导出单例
export const autoTriggerAdapter = new AutoTriggerAdapter();
